import * as analysisState from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack, SvgIcon, SxProps } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { PoiIcon } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/PoiIcon';
import { StyledCheckbox } from '@/components/Analysis/ReviewScreen/PointsOfInterest/SummaryCard/styles';
import { useAnimationFrame, useFeatureFlag } from '@/hooks';
import { PillMenu } from '@/components/shared/PillMenu';
import { PillStatus } from '@/components/shared/Pill/types';
import { accordionStyle, accordionSummaryStyle } from './styles';
import { ActionHeader } from '../../ActionHeader';
import { LocationTag } from '@/constants';

export type StatusViewFilterType = 'internal' | 'inspection' | 'both';

const STATUS_VIEW_FILTER_OPTIONS: { value: StatusViewFilterType; label: string }[] = [
  { value: 'internal', label: 'Internal Status' },
  { value: 'inspection', label: 'Inspector Status' },
  { value: 'both', label: 'Internal Status | Inspector Status' },
];

type PoiTypeAccordionProps = {
  title: string;
  details: (statusViewFilter: StatusViewFilterType) => React.ReactNode;
  templateId: string;
  count: number;
  defaultExpanded: boolean;
  customStyle?: SxProps;
  poiTemplateKey?: string | null | undefined;
};

export const PoiTypeAccordion = ({
  title,
  details,
  templateId,
  count = 0,
  defaultExpanded = false,
  customStyle = {},
  poiTemplateKey,
}: PoiTypeAccordionProps) => {
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);
  const hasAitRole = useFeatureFlag('ait-engineer');
  const hasApiInspectorRole = useFeatureFlag('api-inspector');
  const canShowStatusViewFilter = hasAitRole && hasApiInspectorRole;
  const setSelectedFieldByType = useSetAtom(analysisState.selectedFieldByType);
  const [checkedGroupedPois, setCheckedGroupedPois] = useAtom(analysisState.checkedGroupedPois);
  const [selectedReviewPoiTypes, setSelectedReviewPoiTypes] = useAtom(
    analysisState.selectedReviewPoiTypes
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [statusViewFilter, setStatusViewFilter] = useState<StatusViewFilterType>('both');
  const statusViewFilterLabel =
    STATUS_VIEW_FILTER_OPTIONS.find((o) => o.value === statusViewFilter)?.label ??
    STATUS_VIEW_FILTER_OPTIONS[0].label;

  const isAllChecked = useMemo(
    () => checkedGroupedPois[templateId]?.every((item) => item.checked === true) || false,
    [checkedGroupedPois, templateId]
  );

  const isSomeChecked = useMemo(
    () =>
      (!isAllChecked && checkedGroupedPois[templateId]?.some((item) => item.checked === true)) ||
      false,
    [checkedGroupedPois, templateId, isAllChecked]
  );

  const isTypeSelected = useMemo(
    () => selectedReviewPoiTypes.includes(title) || isAllChecked,
    [selectedReviewPoiTypes, title, isAllChecked]
  );

  useEffect(() => {
    setSelectedFieldByType((prev) => ({
      ...prev,
      [title]: {
        label: 'Status',
        value: 'status',
      },
    }));
  }, [setSelectedFieldByType, title]);

  const isUpdatingRef = useRef(false);

  const handleCheckboxChangeInternal = useCallback(() => {
    // Prevent cascading updates
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    const willBeChecked = !isAllChecked;

    // Batch both state updates together using functional updates
    setCheckedGroupedPois((prev) => {
      const updatePois = prev[templateId]?.map((poi) => ({
        ...poi,
        checked: willBeChecked,
      }));
      return {
        ...prev,
        [templateId]: updatePois,
      };
    });

    setSelectedReviewPoiTypes((prev) => {
      if (willBeChecked) {
        return prev.includes(title) ? prev : [...prev, title];
      } else {
        return prev.filter((item) => item !== title);
      }
    });

    // Reset the updating flag
    isUpdatingRef.current = false;
  }, [templateId, title, isAllChecked, setCheckedGroupedPois, setSelectedReviewPoiTypes]);

  // Wrap with requestAnimationFrame for smooth animations
  const handleCheckboxChangeAnimated = useAnimationFrame(handleCheckboxChangeInternal);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      handleCheckboxChangeAnimated();
    },
    [handleCheckboxChangeAnimated]
  );

  const handleChange = useCallback((event: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{ ...accordionStyle, ...customStyle }}
      id={title}
      disabled={!count}
    >
      <AccordionSummary
        sx={accordionSummaryStyle}
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '2rem' }} />}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flex={1}
          gap="0.8rem"
        >
          <Stack alignItems="center" direction="row" gap="0.8rem">
            <StyledCheckbox
              disableRipple
              indeterminate={isSomeChecked}
              checked={isTypeSelected}
              checkboxSize="md"
              onChange={handleCheckboxChange}
              onClick={(event) => event.stopPropagation()}
            />
            <SvgIcon sx={{ color: abyssColors.primary[300], fontSize: '1.6rem' }}>
              <PoiIcon name={title} poiTemplateKey={poiTemplateKey} />
            </SvgIcon>
            <Typography variant="caption" fontWeight={500}>
              {title}
            </Typography>
          </Stack>
          <Typography variant="body1" fontWeight={500} lineHeight="2.4rem">
            {count}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1.2}>
          <ActionHeader
            variant="compact"
            poiType={title}
            templateId={templateId}
            location={LocationTag.ViewerPoiList}
            leftContent={
              canViewReviewScreen && canShowStatusViewFilter ? (
                <PillMenu
                  key={`status-view-filter-${templateId}`}
                  size="small"
                  options={STATUS_VIEW_FILTER_OPTIONS.map((o) => o.label)}
                  initialValue={statusViewFilterLabel}
                  label={statusViewFilterLabel}
                  getPillStatus={(): PillStatus => 'disabled'}
                  loading={false}
                  loadingLabel=""
                  onChange={(label) => {
                    const option = STATUS_VIEW_FILTER_OPTIONS.find((o) => o.label === label);
                    if (option) setStatusViewFilter(option.value);
                  }}
                  className="viewer-poi-list-status-view-filter"
                />
              ) : undefined
            }
          />
          {details(statusViewFilter)}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
